import React, { useCallback, useEffect, useState } from 'react';
import { EntityHoverCard } from './EntityHoverCard';
import { useEntityHover } from './useEntityHover';
import { entityService } from '../../services/entityService';
import { useEntityIcon } from '../../hooks/useEntityIcon';
import { useCommandRegistryStore } from '../../stores/commandRegistryStore';
import './InlineEntity.scss';

// ---------------------------------------------------------------------------
// EntityHoverTrigger — makes any children hoverable with an entity popover.
// Renders no chrome of its own; children are rendered as-is.
// ---------------------------------------------------------------------------

interface EntityHoverTriggerProps {
  entityId: string;
  entityType?: string;
  /** Pass already-loaded entity data to avoid a fetch inside the card. */
  preloadedEntity?: any;
  children: React.ReactNode;
  className?: string;
}

export const EntityHoverTrigger: React.FC<EntityHoverTriggerProps> = ({
  entityId,
  entityType,
  preloadedEntity,
  children,
  className,
}) => {
  const {
    triggerRef,
    isOpen,
    anchor,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
  } = useEntityHover();

  return (
    <>
      <span
        ref={triggerRef as React.RefObject<HTMLSpanElement>}
        className={`entity-hover-trigger${className ? ` ${className}` : ''}`}
        onMouseEnter={onTriggerMouseEnter}
        onMouseLeave={onTriggerMouseLeave}
      >
        {children}
      </span>
      {isOpen && anchor && (
        <EntityHoverCard
          entityId={entityId}
          entityType={entityType}
          preloadedEntity={preloadedEntity}
          anchor={anchor}
          onClose={onTriggerMouseLeave}
          onCardMouseEnter={onCardMouseEnter}
          onCardMouseLeave={onCardMouseLeave}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// InlineEntity — a small chip that represents an entity inline in text or UI.
// Hovering shows the entity popover. Clicking opens the entity in a window.
// ---------------------------------------------------------------------------

interface InlineEntityProps {
  entityId: string;
  entityType?: string;
  /** If provided, skips fetching entity for the chip label/icon. */
  preloadedEntity?: any;
  /** Override the displayed label. */
  children?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

export const InlineEntity: React.FC<InlineEntityProps> = ({
  entityId,
  entityType,
  preloadedEntity,
  children,
  onClick,
  className,
}) => {
  const {
    triggerRef,
    isOpen,
    anchor,
    onTriggerMouseEnter,
    onTriggerMouseLeave,
    onCardMouseEnter,
    onCardMouseLeave,
  } = useEntityHover();
  const { getEntityIcon } = useEntityIcon({ size: 12 });
  const [entity, setEntity] = useState<any>(preloadedEntity ?? null);

  useEffect(() => {
    if (preloadedEntity) { setEntity(preloadedEntity); return; }
    let cancelled = false;
    entityService.get(entityId)
      .then((e) => { if (!cancelled) setEntity(e); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [entityId, preloadedEntity]);

  const handleClick = useCallback(() => {
    if (onClick) { onClick(); return; }
    useCommandRegistryStore.getState()
      .executeCommand('space_open_window', { entityId })
      .catch(() => {});
  }, [entityId, onClick]);

  const label = children ?? entity?.name ?? '…';
  const icon = entity ? getEntityIcon(entity) : null;
  const isClickable = !!(onClick || entityId);

  return (
    <>
      <span
        ref={triggerRef as React.RefObject<HTMLSpanElement>}
        className={`inline-entity${isClickable ? ' inline-entity--clickable' : ''}${className ? ` ${className}` : ''}`}
        onMouseEnter={onTriggerMouseEnter}
        onMouseLeave={onTriggerMouseLeave}
        onClick={isClickable ? handleClick : undefined}
        role={isClickable ? 'button' : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onKeyDown={isClickable ? (e) => { if (e.key === 'Enter') handleClick(); } : undefined}
      >
        {icon && <span className="inline-entity__icon">{icon}</span>}
        <span className="inline-entity__name">{label}</span>
      </span>
      {isOpen && anchor && (
        <EntityHoverCard
          entityId={entityId}
          entityType={entityType ?? entity?.entityType}
          preloadedEntity={entity}
          anchor={anchor}
          onClose={onTriggerMouseLeave}
          onCardMouseEnter={onCardMouseEnter}
          onCardMouseLeave={onCardMouseLeave}
        />
      )}
    </>
  );
};
